"use client";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRef, useState } from "react";
import { Typography } from "@/components/ui/typography";
import { LanguageLabelLink } from "@/components/screen/global-components/language";
import { useAnimation, motion } from "framer-motion";
import { ROUTES } from "@/constants";
import { normalizeUrl } from "@/utils/normalizeUrl";

type HomeProps = {
  content: {
    logoSection?: AssetLink[] | undefined;
  };
};

type AssetLink = {
  sys: {
    id: string;
    linkType: "Entry";
    type: "Link";
  };
  fields?: {
    title?: string;
    file?: {
      url?: string;
    };
  };
};
const languages = [
  { label: "Afrikaans", slug: "afrikaans" },
  { label: "Albanian", slug: "albanian" },
  { label: "Amharic", slug: "amharic" },
  { label: "Arabic", slug: "arabic" },
  { label: "Armenian", slug: "armenian" },
  { label: "Azerbaijani", slug: "azerbaijani" },
  { label: "Basque", slug: "basque" },
  { label: "Belarusian", slug: "belarusian" },
  { label: "Bengali", slug: "bengali" },
  { label: "Bosnian", slug: "bosnian" },
  { label: "Bulgarian", slug: "bulgarian" },
  { label: "Catalan", slug: "catalan" },
  { label: "Cantonese", slug: "chinese-cantonese" },
  { label: "Chinese (Simplified)", slug: "chinese-simplified" },
  { label: "Chinese (Traditional)", slug: "chinese-traditional" },
  { label: "Croatian", slug: "croatian" },
  { label: "Czech", slug: "czech" },
  { label: "Danish", slug: "danish" },
  { label: "Dutch", slug: "dutch" },
  { label: "English", slug: "english" },
  { label: "Estonian", slug: "estonian" },
  { label: "Finnish", slug: "finnish" },
  { label: "French", slug: "french" },
  { label: "French (Canadian)", slug: "french-canadian" },
  { label: "Georgian", slug: "georgian" },
  { label: "German", slug: "german" },
  { label: "Greek", slug: "greek" },
  { label: "Gujarati", slug: "gujarati" },
  { label: "Hebrew", slug: "hebrew" },
  { label: "Hindi", slug: "hindi" },
  { label: "Hungarian", slug: "hungarian" },
  { label: "Icelandic", slug: "icelandic" },
  { label: "Indonesian", slug: "indonesian" },
  { label: "Irish", slug: "irish" },
  { label: "Italian", slug: "italian" },
  { label: "Japanese", slug: "japanese" },
  { label: "Kannada", slug: "kannada" },
  { label: "Kazakh", slug: "kazakh" },
  { label: "Khmer", slug: "khmer" },
  { label: "Korean", slug: "korean" },
  { label: "Kurdish", slug: "kurdish" },
  { label: "Kyrgyz", slug: "kyrgyz" },
  { label: "Lao", slug: "lao" },
  { label: "Latvian", slug: "latvian" },
  { label: "Lithuanian", slug: "lithuanian" },
  { label: "Macedonian", slug: "macedonian" },
  { label: "Malay", slug: "malay" },
  { label: "Maltese", slug: "maltese" },
  { label: "Mandarin", slug: "chinese-mandarin" },
  { label: "Marathi", slug: "marathi" },
  { label: "Mongolian", slug: "mongolian" },
  { label: "Nepali", slug: "nepali" },
  { label: "Norwegian", slug: "norwegian" },
  { label: "Persian", slug: "farsi" },
  { label: "Polish", slug: "polish" },
  { label: "Portuguese (Brazil)", slug: "brazilian-portuguese" },
  { label: "Portuguese (Portugal)", slug: "portuguese" },
  { label: "Punjabi", slug: "punjabi" },
  { label: "Romanian", slug: "romanian" },
  { label: "Russian", slug: "russian" },
  { label: "Serbian", slug: "serbian" },
  { label: "Slovak", slug: "slovak" },
  { label: "Slovenian", slug: "slovenian" },
  { label: "Somali", slug: "somali" },
  { label: "Spanish", slug: "spanish" },
  { label: "Swahili", slug: "swahili" },
  { label: "Swedish", slug: "swedish" },
  { label: "Tajik", slug: "tajik" },
  { label: "Tamil", slug: "tamil" },
  { label: "Telugu", slug: "telugu" },
  { label: "Thai", slug: "thai" },
  { label: "Turkish", slug: "turkish" },
  { label: "Turkmen", slug: "turkmen" },
  { label: "Ukrainian", slug: "ukrainian" },
  { label: "Urdu", slug: "urdu" },
  { label: "Uzbek", slug: "uzbek" },
  { label: "Vietnamese", slug: "vietnamese" },
  { label: "Welsh", slug: "welsh" },
  { label: "Yiddish", slug: "yiddish" },
];

export default function TranslationBlockLanguage({ content }: HomeProps) {
  //   const logoData = content.logoSection && content?.logoSection;\

  const [expanded, setExpanded] = useState(false);
  const [fromLanguage, setFromLanguage] = useState<string>("english");
  const [toLanguage, setToLanguage] = useState<string>("");
  const controls = useAnimation();
  const contentRef = useRef<HTMLDivElement>(null);

  const handleExpand = () => {
    if (contentRef.current) {
      const fullHeight = contentRef.current.scrollHeight;
      controls
        .start({
          maxHeight: fullHeight,
          transition: { duration: 0.5, ease: "easeInOut" },
        })
        .then(() => {
          setExpanded(true);
        });
    }
  };

  return (
    <section className="section">
      <div
        className={cn("container py-12 lg:py-20 mx-auto", {
          "lg:pb-0": !expanded && languages.length > 14,
        })}
      >
        <div className="flex flex-col justify-center gap-4 md:gap-2">
          <Typography variant="heading4" className="text-center">
            <h2>Expert certified translation in 100+ languages</h2>
          </Typography>
          <div className="flex justify-center text-gray-600 md:w-full md:max-w-[685] md:mx-auto">
            <Typography variant="subHeadingSm" className="text-center" asChild>
              <p>
                Grow your multilingual communication using the most elite
                network of native-language translators with industry and
                use-case specialization for exceptional accuracy in every
                language.
              </p>
            </Typography>
          </div>
        </div>

        <div className="mt-6 lg:mt-[53] flex p-6 w-full max-w-[960] mx-auto border border-neutral-100 bg-background-secondary rounded-3xl md:rounded-2xl md:bg-cloud-gray md:border-0">
          <div className="flex-auto min-w-0 grid grid-cols-1 gap-4 md:flex md:gap-2.5">
            <div className="flex flex-col md:flex-1 md:min-w-0">
              <Typography variant="labelSm" className="md:font-medium">
                <span>From</span>
              </Typography>
              <div className="mt-2">
                <Select value={fromLanguage} onValueChange={setFromLanguage}>
                  <SelectTrigger
                    className="w-full !h-[60px] px-7.5 !text-semantic-text-primary
                  cursor-pointer bg-white rounded-full data-[state=open]:!border-transparent
                  hover:border-1 border-1 !text-base lg:!text-sm"
                  >
                    <SelectValue placeholder="English" />
                  </SelectTrigger>
                  <SelectContent
                    className="overflow-y-auto rounded-2xl"
                    position="popper"
                    align="center"
                    style={
                      {
                        "--radix-select-trigger-width":
                          "min(calc(100vw-48px),860px)",
                        maxWidth:
                          "calc(var(--radix-popper-available-width) - 32px)",
                        maxHeight:
                          "var(--radix-select-content-available-height)",
                      } as React.CSSProperties
                    }
                  >
                    <SelectGroup>
                      <SelectLabel className="px-2 py-1.5 text-sm font-medium">Languages</SelectLabel>
                      {languages.map(({ label, slug }) => (
                        <SelectItem 
                          key={slug} 
                          value={slug}
                          className="cursor-pointer"
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col md:flex-1 md:min-w-0">
              <Typography variant="labelSm" className="md:font-medium">
                <span>To</span>
              </Typography>
              <div className="mt-2 relative">
                <Select value={toLanguage} onValueChange={setToLanguage}>
                  <SelectTrigger
                    className="w-full !h-[60px] px-7.5 !text-semantic-text-primary
                  cursor-pointer bg-white rounded-full data-[state=open]:!border-transparent
                  hover:border-1 border-1 !text-base lg:!text-sm"
                  >
                    <SelectValue placeholder="Choose Language" />
                  </SelectTrigger>
                  <SelectContent
                    className="overflow-y-auto rounded-2xl"
                    position="popper"
                    align="center"
                    style={
                      {
                        "--radix-select-trigger-width":
                          "min(calc(100vw-48px),860px)",
                        maxWidth:
                          "calc(var(--radix-popper-available-width) - 32px)",
                        maxHeight:
                          "var(--radix-select-content-available-height)",
                      } as React.CSSProperties
                    }
                  >
                    <SelectGroup>
                      <SelectLabel className="px-2 py-1.5 text-sm font-medium">Languages</SelectLabel>
                      {languages.map(({ label, slug }) => (
                        <SelectItem 
                          key={slug} 
                          value={slug}
                          className="cursor-pointer"
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex md:max-w-[216] md:w-full">
              <Button
                asChild
                variant="primary"
                className="flex-auto min-w-0 h-[48px] md:h-[60px] md:mt-auto md:rounded-4xl"
              >
                <Link href={ROUTES.startOrder} className="!text-base">
                  Start order
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6 lg:mt-[50] overflow-hidden">
          <motion.div
            initial={{ maxHeight: 300 }}
            animate={controls}
            className={cn(
              "relative overflow-hidden before:absolute before:pointer-events-none before:bg-mask-gradient before:inset-x-0 before:top-0 before:-bottom-[127px] lg:before:-bottom-20 lg:before:-top-[180px] before:will-change-opacity before:transition-[opacity]",
              {
                "before:hidden": languages.length <= 14,
                "max-h-[300]": languages.length > 14,
                "before:opacity-0": expanded,
                "!max-h-none": expanded || languages.length <= 14,
              }
            )}
            ref={contentRef}
          >
            <div className="flex flex-wrap justify-center gap-2 md:gap-4">
              {languages.map(({ label, slug }, i) => {
                return (
                  <LanguageLabelLink key={slug + i} href={normalizeUrl(`/languages/${slug}`)}>
                    {label}
                  </LanguageLabelLink>
                );
              })}
            </div>
          </motion.div>
          {!expanded && (
            <div
              className={cn(
                "relative mt-6 lg:mt-0 flex justify-center lg:transform lg:-translate-y-[92px] lg:-mb-[70px]",

                {
                  hidden: languages.length <= 14,
                }
              )}
            >
              <Button
                onClick={handleExpand}
                variant="primary-outline"
                className={
                  "cursor-pointer flex-auto min-w-0 sm:flex-initial py-3 px-6 h-[48px] w-full sm:w-full sm:max-w-[209]"
                }
              >
                <span>Explore all languages</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
